import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ColourPicker } from '../../components/ColourPicker.js'
import { Icon } from '../../components/Icon.js'
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  SkeletonRows,
} from '../../components/ui.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'

/**
 * The board's furniture: which sections staff are grouped into, and which
 * buckets unbooked work can be parked in.
 *
 * Both are lookup tables rather than enums for the same reason. "North, South,
 * East, West" is what the business says today; the first Gold Coast crew or the
 * first "Pending station checks" backlog would otherwise be a migration, and a
 * migration is not a thing a dispatcher can do on a Tuesday.
 */

/** Sky for a section, amber for a bucket, so a fresh one is not another grey. */
const NEW_GROUP_COLOUR = '#0ea5e9'
const NEW_BUCKET_COLOUR = '#f59e0b'

export function BoardPanel() {
  return (
    <>
      <GroupsCard />
      <BucketsCard />
    </>
  )
}


/**
 * Move up and Move down, for the order the board reads its rows in.
 *
 * Both lists sorted by `sortOrder` and nothing could change it after the row
 * was made, so the board read the sections in the order they were typed in.
 * A move rewrites the whole list's positions in tens rather than swapping two
 * numbers, which also mends lists that arrived with every row at zero.
 */
function moveWrites<T extends { id: string; sortOrder: number }>(
  rows: T[],
  index: number,
  delta: -1 | 1,
): Array<{ id: string; sortOrder: number }> {
  const target = index + delta
  if (target < 0 || target >= rows.length) return []
  const next = [...rows]
  const [moved] = next.splice(index, 1)
  next.splice(target, 0, moved!)
  return next
    .map((row, position) => ({ id: row.id, sortOrder: position * 10, was: row.sortOrder }))
    .filter((row) => row.sortOrder !== row.was)
    .map(({ id, sortOrder }) => ({ id, sortOrder }))
}

function OrderButtons({
  name,
  first,
  last,
  onMove,
}: {
  name: string
  first: boolean
  last: boolean
  onMove: (delta: -1 | 1) => void
}) {
  return (
    <span className="flex shrink-0 flex-col">
      <button
        type="button"
        aria-label={`Move ${name} up`}
        disabled={first}
        onClick={() => onMove(-1)}
        className="grid h-4 w-6 place-items-center rounded text-ink-500 hover:bg-ink-100 hover:text-ink-900 disabled:opacity-30"
      >
        <Icon name="chevronDown" className="size-3.5 rotate-180" />
      </button>
      <button
        type="button"
        aria-label={`Move ${name} down`}
        disabled={last}
        onClick={() => onMove(1)}
        className="grid h-4 w-6 place-items-center rounded text-ink-500 hover:bg-ink-100 hover:text-ink-900 disabled:opacity-30"
      >
        <Icon name="chevronDown" className="size-3.5" />
      </button>
    </span>
  )
}

function GroupsCard() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const groups = useQuery(trpc.users.scheduleGroups.queryOptions())
  const staff = useQuery(trpc.users.schedulable.queryOptions())

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.users.pathFilter())
    void queryClient.invalidateQueries(trpc.schedule.pathFilter())
  }

  const create = useMutation(trpc.users.createScheduleGroup.mutationOptions({ onSuccess: invalidate }))
  const update = useMutation(trpc.users.updateScheduleGroup.mutationOptions({ onSuccess: invalidate }))
  const remove = useMutation(trpc.users.deleteScheduleGroup.mutationOptions({ onSuccess: invalidate }))
  const assign = useMutation(trpc.users.setScheduleGroup.mutationOptions({ onSuccess: invalidate }))

  const [name, setName] = useState('')
  const [colour, setColour] = useState(NEW_GROUP_COLOUR)
  const [isAdmin, setIsAdmin] = useState(false)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim()) return
    create.mutate(
      { name: name.trim(), colour, sortOrder: (groups.data?.length ?? 0) * 10, isAdmin },
      { onSuccess: () => setName('') },
    )
  }

  const counts = new Map<string, number>()
  for (const person of staff.data ?? []) {
    if (!person.scheduleGroupId) continue
    counts.set(person.scheduleGroupId, (counts.get(person.scheduleGroupId) ?? 0) + 1)
  }
  const ungrouped = (staff.data ?? []).filter((person) => !person.scheduleGroupId)

  return (
    <Card>
      <CardHeader title="Board sections" />
      <p className="px-[var(--card-pad)] pt-2 text-xs text-ink-500">
        How staff are grouped on the dispatch board. Office sections sort above areas.
      </p>

      {groups.isPending ? (
        <SkeletonRows rows={3} />
      ) : (
        <ul className="divide-y divide-ink-100">
          {(groups.data ?? []).map((group, index, all) => (
            <li
              key={group.id}
              className="flex items-center gap-2 px-[var(--row-pad-x)] py-[var(--row-pad-y)]"
            >
              <OrderButtons
                name={group.name}
                // Office sections always sort above areas, so a move stays on
                // its own side of that line.
                first={index === 0 || all[index - 1]!.isAdmin !== group.isAdmin}
                last={index === all.length - 1 || all[index + 1]!.isAdmin !== group.isAdmin}
                onMove={(delta) => {
                  for (const write of moveWrites(all, index, delta)) update.mutate(write)
                }}
              />
              <ColourPicker
                value={group.colour}
                onChange={(next) => update.mutate({ id: group.id, colour: next })}
              />

              <Input
                defaultValue={group.name}
                aria-label={`Name of ${group.name}`}
                className="min-w-0 flex-1"
                onBlur={(event) => {
                  const next = event.target.value.trim()
                  if (next && next !== group.name) update.mutate({ id: group.id, name: next })
                }}
              />

              <label className="flex shrink-0 items-center gap-1.5 text-xs text-ink-600">
                <input
                  type="checkbox"
                  checked={group.isAdmin}
                  onChange={(event) =>
                    update.mutate({ id: group.id, isAdmin: event.target.checked })
                  }
                />
                Office
              </label>

              <span className="w-16 shrink-0 text-right text-xs tabular-nums text-ink-500">
                {counts.get(group.id) ?? 0} staff
              </span>

              <Button
                size="sm"
                variant="ghost"
                aria-label={`Remove ${group.name}`}
                loading={remove.isPending}
                onClick={() => remove.mutate({ id: group.id })}
              >
                <Icon name="trash" className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex flex-wrap items-end gap-2 p-[var(--card-pad)]">
        <div className="min-w-40 flex-1">
          <Field label="New section" htmlFor="group-name">
            <Input
              id="group-name"
              value={name}
              placeholder="North"
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
        </div>

        <ColourPicker value={colour} onChange={setColour} />

        <label className="flex h-[var(--control-md)] items-center gap-1.5 text-xs text-ink-600">
          <input
            type="checkbox"
            checked={isAdmin}
            onChange={(event) => setIsAdmin(event.target.checked)}
          />
          Office
        </label>

        <Button type="submit" variant="secondary" loading={create.isPending}>
          Add
        </Button>
      </form>

      {create.error ? (
        <p className="px-[var(--card-pad)] pb-3 text-sm text-red-700">
          {errorMessage(create.error)}
        </p>
      ) : null}

      {ungrouped.length > 0 ? (
        <div className="border-t border-ink-100 p-[var(--card-pad)]">
          <p className="mb-2 text-xs font-medium text-ink-700">
            Not in a section yet — they appear under “Everyone else”.
          </p>
          <ul className="flex flex-col gap-1.5">
            {ungrouped.map((person) => (
              <li key={person.id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm text-ink-800">{person.name}</span>
                <select
                  aria-label={`Section for ${person.name}`}
                  defaultValue=""
                  className="h-[var(--control-sm)] rounded-md border border-ink-200 bg-white px-2 text-xs"
                  onChange={(event) =>
                    assign.mutate({
                      userId: person.id,
                      scheduleGroupId: event.target.value || null,
                    })
                  }
                >
                  <option value="">No section</option>
                  {(groups.data ?? []).map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  )
}

function BucketsCard() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  // One job per bucket is enough to count them; the board asks for the contents.
  const buckets = useQuery(trpc.schedule.buckets.queryOptions({ perBucket: 1 }))

  const invalidate = () => void queryClient.invalidateQueries(trpc.schedule.pathFilter())

  const create = useMutation(trpc.schedule.createBucket.mutationOptions({ onSuccess: invalidate }))
  const update = useMutation(trpc.schedule.updateBucket.mutationOptions({ onSuccess: invalidate }))
  const remove = useMutation(trpc.schedule.deleteBucket.mutationOptions({ onSuccess: invalidate }))

  const [name, setName] = useState('')
  const [colour, setColour] = useState(NEW_BUCKET_COLOUR)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim()) return
    create.mutate(
      { name: name.trim(), colour, sortOrder: (buckets.data?.length ?? 0) * 10 },
      { onSuccess: () => setName('') },
    )
  }

  return (
    <Card>
      <CardHeader title="Parking buckets" />
      <p className="px-[var(--card-pad)] pt-2 text-xs text-ink-500">
        Named places to file work that has no date. Nothing in a bucket is booked.
      </p>

      {buckets.isPending ? (
        <SkeletonRows rows={3} />
      ) : (buckets.data ?? []).length === 0 ? (
        <EmptyState
          title="No buckets yet"
          description="Add one for the work that keeps getting put off, like annual termite treatments."
        />
      ) : (
        <ul className="divide-y divide-ink-100">
          {(buckets.data ?? []).map((bucket, index, all) => (
            <li
              key={bucket.id}
              className="flex items-center gap-2 px-[var(--row-pad-x)] py-[var(--row-pad-y)]"
            >
              <OrderButtons
                name={bucket.name}
                first={index === 0}
                last={index === all.length - 1}
                onMove={(delta) => {
                  for (const write of moveWrites(all, index, delta)) update.mutate(write)
                }}
              />
              <ColourPicker
                value={bucket.colour}
                onChange={(next) => update.mutate({ id: bucket.id, colour: next })}
              />

              <Input
                defaultValue={bucket.name}
                aria-label={`Name of ${bucket.name}`}
                className="min-w-0 flex-1"
                onBlur={(event) => {
                  const next = event.target.value.trim()
                  if (next && next !== bucket.name) update.mutate({ id: bucket.id, name: next })
                }}
              />

              <span className="w-20 shrink-0 text-right text-xs tabular-nums text-ink-500">
                {bucket.total} parked
              </span>

              <Button
                size="sm"
                variant="ghost"
                aria-label={`Remove ${bucket.name}`}
                loading={remove.isPending}
                // Deliberately not a confirm: nothing is lost. The jobs go back
                // to the rail, which is where they were before anyone filed them.
                title="Retire this bucket. Its jobs return to Needs a slot."
                onClick={() => remove.mutate({ id: bucket.id })}
              >
                <Icon name="trash" className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex flex-wrap items-end gap-2 p-[var(--card-pad)]">
        <div className="min-w-40 flex-1">
          <Field label="New bucket" htmlFor="bucket-name">
            <Input
              id="bucket-name"
              value={name}
              placeholder="Annual termite treatments"
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
        </div>

        <ColourPicker value={colour} onChange={setColour} />

        <Button type="submit" variant="secondary" loading={create.isPending}>
          Add
        </Button>
      </form>

      {create.error ? (
        <p className="px-[var(--card-pad)] pb-3 text-sm text-red-700">
          {errorMessage(create.error)}
        </p>
      ) : null}
    </Card>
  )
}
